// React imports
import React, { useEffect } from 'react';

// React Router imports
import { Link, useNavigate } from 'react-router-dom';

// AWS Amplify imports
import { signOut } from 'aws-amplify/auth';

// Redux imports
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { clearUser } from '../../redux/userSlice';
import { clearUserTypes } from '../../redux/userTypesSlice';
import { clearDepartments } from '../../redux/departmentsSlice';
import { clearSupports } from '../../redux/supportSlice';
import { clearAllIPStatuses } from '../../redux/ipStatusesSlice';
import { clearSchools } from '../../redux/schoolsSlice';

// Utility imports
import { getGmailUrl } from '../../utils/CommonUtils';
import { toast } from 'react-toastify';

// Constant imports
import { FACULTY, STAFF } from '../../constants/Constants';
import { SUPPORT_EMAIL } from '../../constants/Constants';

// Asset imports (images and icons)
import SignOutArrow from '../../assets/sign-out-arrow.png';
import cross from '../../assets/cross.png';
import { ReactComponent as BuiltLogo } from '../../assets/built-logo.svg';
import { ReactComponent as RightArrow } from '../../assets/right-arrow.svg';

// Style imports
import '../../styles/NavigationDrawer.css';

// Component imports
import Button from './Button';

interface PrimaryNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrimaryNavigation: React.FC<PrimaryNavigationProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const isFacultyOrStaff =
    user.userDetails?.userTypeId === FACULTY ||
    user.userDetails?.userTypeId === STAFF;
  useEffect(() => {
    const updateIsMobile = () => {
      if (window.innerWidth <= 576 && isOpen) {
        onClose();
      }
    };
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, [isOpen, onClose]);

  const handleDrawerClose = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (
      event.type === 'click' ||
      (event as React.KeyboardEvent).key === 'Enter' ||
      (event as React.KeyboardEvent).key === ' '
    ) {
      onClose();
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSignout = async () => {
    try {
      await signOut();
      dispatch(clearUser());
      dispatch(clearUserTypes());
      dispatch(clearSupports());
      dispatch(clearDepartments());
      dispatch(clearSchools());
      dispatch(clearAllIPStatuses());
      navigate('/');
    } catch (error) {
      console.error('error signing out: ', error);
      toast.error('Logout failed, please try again.');
    }
  };

  const mailToSupport = () => {
    window.open(
      getGmailUrl(
        SUPPORT_EMAIL,
        'Request for Assistance with BUILT',
        `Hi BUILT Team, \n\nTell us about the issue you're facing. Include details like a screenshot, specific page, or any relevant information. We're here to help!\n\nBest regards,\n${user.userDetails?.name}`,
        null,
      ),
      '_blank',
    );
  };

  const navMenuItems = [
    { label: 'Ideas', path: '/dashboard', showArrow: true },
    { label: 'People', path: '/people', showArrow: true },
    { isHr: true },
    isFacultyOrStaff && {
      label: 'My Ideas',
      path: '/my-ideas',
      showArrow: true,
    },
    {
      label: 'My Profile',
      path: `/profile/${user.userDetails?.userId}`,
      showArrow: true,
    },
    { label: 'Following', path: '/following', showArrow: true },
    { isHr: true },
    { label: 'Get Help', action: mailToSupport },
    {
      label: 'Terms and Conditions',
      path: '/terms-and-conditions',
    },
    { isHr: true },
    {
      label: 'Sign Out',
      action: handleSignout,
      isSignOut: true,
      showArrow: false,
    },
  ];

  return (
    <div
      className={`navigation-drawer ${isOpen ? 'open' : ''}`}
      aria-hidden={!isOpen}
    >
      <div className="navigation-drawer-sub">
        <div className="navigation-header">
          <div className="left-nav-logo">
            <BuiltLogo fill="white" />
          </div>
        </div>
        <div className="navigation-links-container grid gap-25">
          <ul className="main-menu grid gap-20 m-0">
            {navMenuItems.map((item, index) =>
              item ? (
                item.isHr ? (
                  <hr key={index} className="horizontal-line" />
                ) : (
                  <li key={index}>
                    <Link
                      to={item.path || '#'}
                      onClick={(e) => {
                        e.preventDefault();
                        if (item.action) {
                          item.action();
                        } else if (item.path) {
                          handleNavigate(item.path);
                        }
                      }}
                      className="nav-links"
                    >
                      {item.label}
                      {item.showArrow && (
                        <RightArrow stroke="var(--primary-color)" />
                      )}
                      {item.isSignOut && (
                        <img
                          src={SignOutArrow}
                          alt="Sign Out"
                          className="arrow-icon"
                        />
                      )}
                    </Link>
                  </li>
                )
              ) : null,
            )}
          </ul>
          <div className="button-container d-flex gap-10">
            {isFacultyOrStaff && (
              <Button
                type="button"
                className="primary-button"
                onClick={() => handleNavigate('/idea-creation')}
              >
                Post Your Idea
              </Button>
            )}
            <Button
              type="button"
              className="secondary-button"
              onClick={() => handleNavigate('/invite-others')}
            >
              Invite Others
            </Button>
          </div>
        </div>
      </div>
      <div className="close-icon-container">
        <button
          type="button"
          className="close-button"
          onClick={handleDrawerClose}
          // onKeyDown={handleDrawerClose}
          aria-label="Close navigation drawer"
        >
          <img src={cross} alt="cross-button" className="cross-icon" />
        </button>
      </div>
    </div>
  );
};

export default PrimaryNavigation;
